import React, { useEffect, useState, useRef } from 'react';
import {
  Typography,
  Box,
  CircularProgress,
  Paper,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../core/axiosInstance';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import Divider from '@mui/material/Divider';
import liff from '@line/liff';
// Utility function to generate a GUID
const createGuid = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

const UploadTransferSlip = () => {
  // State declarations
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [amount, setAmount] = useState('');
  const [selectedBank, setSelectedBank] = useState('');
  const [paymentDateTime, setPaymentDateTime] = useState(new Date());

  const navigate = useNavigate();
  const { id } = useParams();

  // Reference for the hidden file input
  const fileInputRef = useRef(null);

  // Generate preview URL when a file is selected
  useEffect(() => {
    if (!selectedFile) return;
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(selectedFile);
  }, [selectedFile]);

  // Fetch contract data on mount or when ID changes
  useEffect(() => {
    const fetchContract = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/api/contracts/me?contractId=${id}`);
        setContractData(response.data[0]);
      } catch (err) {
        console.error('Error fetching contract:', err);
        setError('Failed to load contract data');
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [id]);

  // Fetch bank list when contract data is available
  useEffect(() => {
    if (contractData) {
      liff
        .init({
          liffId: import.meta.env.VITE_LIFF_ID,
        })
        .then(async () => {
          console.log('LIFF init succeeded');
        })
        .catch((e) => {
          alert(`พบข้อผิดพลาด: ${e.message}`);
        });

      const fetchBanks = async () => {
        try {
          const companyId = contractData.companyId;
          const bankResponse = await api.get(
            `/api/bankaccount/bycompanyid/${companyId}`
          );

          const qrResponse = await api.get(
            `/api/company/${contractData.companyId}`
          );
          const company = qrResponse.data;
          setCompanyData(company);

          // Combine company branch as first option with bank list
          const activeBanks = bankResponse.data.filter((bank) => bank.isActive);
          const combinedBankList = [
            {
              id: 0,
              bankName: `(ธนาคารสาขา) ${company.qrBankAccountName}`,
              accountName: company.qrBankAccountName,
              accountNumber: company.qrBankAccountNumber,
            },
            ...activeBanks,
          ];

          setBankList(combinedBankList);
        } catch (err) {
          console.error('Error fetching banks:', err);
          setError('Failed to load bank list');
        }
      };
      fetchBanks();
    }
  }, [contractData]);

  // Handlers
  const handleBackClick = () => navigate(-1);

  const b64toBlob = (b64Data, contentType, sliceSize) => {
    contentType = contentType || '';
    sliceSize = sliceSize || 512;
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !amount || selectedBank === '') return;

    setUploadLoading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('contractId', id);
      const fname = Math.random().toString(20).substr(2, 8) + '.png';
      formData.append('fileName', fname);

      formData.append(
        'file',
        b64toBlob(previewUrl.split(',')[1], 'image/png', 512),
        fname
      );

      const slipResponse = await api.post('/api/files/upload', formData);
      const slipId = slipResponse.data.id;

      const paymentData = {
        sessionId: createGuid(),
        slipId: slipId,
        amount: parseFloat(amount),
        bankId: selectedBank,
        paymentDate: format(paymentDateTime, 'yyyy-MM-dd HH:mm'),
        contractId: id,
        isMultiple: false,
        source: selectedBank === 0 ? 1 : 0,
      };

      const response = await api.post('/api/paymentrequest', paymentData);

      if (response.status === 200) {
        Swal.fire({
          icon: 'success',
          title:
            'บริษัทจะดำเนินงานให้ และ update ใบเสร็จไม่เกิน 1 วัน (วันทำการ)',
          html: 'กรณีท่านไม่ได้ update กรุณาติดต่อบริษัท<br /><br /><i>คำเตือน การบันทึกสลิปในใบโอนปลอมมีความผิดทางกฎหมายอาญา</i>',
        }).then(() => {
          handleBackClick();
        });

        handleRemoveFile();
        setAmount('');
        setSelectedBank('');
        setPaymentDateTime(new Date());

        const context = liff.getContext();

        if (
          context &&
          (context.type === 'utou' ||
            context.type === 'room' ||
            context.type === 'group')
        ) {
          liff
            .sendMessages([
              {
                type: 'text',
                text: `แจ้งชำระเงิน สัญญาเลขที่ ${contractData.contractNumber} จำนวน ${amount} บาท`,
              },
            ])
            .then(() => {
              // nothing
            })
            .catch((err) => {
              throw err;
            });
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      setUploadError('Failed to upload transfer slip');
      Swal.fire({
        icon: 'error',
        title: 'Upload Failed',
        text: 'Failed to upload transfer slip. Please try again.',
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // Render
  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="fixed" sx={{ bgcolor: '#1976d2' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBackClick}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            แจ้งชำระเงิน
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ pt: 8, pb: 2, display: 'flex', justifyContent: 'center' }}>
        <Paper
          elevation={3}
          sx={{
            width: '100%',
            maxWidth: 480,
            borderRadius: 2,
            overflow: 'hidden',
            m: { xs: 2, sm: 3 },
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {error && (
              <Typography color="error" sx={{ my: 2 }}>
                {error}
              </Typography>
            )}

            {/* File Input Section */}
            <Typography variant="h6" sx={{ mb: 2 }}>
              กรุณาแนบสลิปการโอนเงิน
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Button
                variant="contained"
                component="span"
                onClick={() => fileInputRef.current.click()}
                startIcon={<CloudUploadIcon />}
                sx={{ mr: 2 }}
              >
                เลือกไฟล์
              </Button>
              <Typography variant="body2">
                {selectedFile
                  ? `ไฟล์ที่เลือก: ${selectedFile.name}`
                  : 'ยังไม่มีไฟล์ที่เลือก'}
              </Typography>
            </Box>
            <input
              type="file"
              accept="image/png, image/jpeg"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => setSelectedFile(e.target.files[0])}
            />

            {/* Payment Details */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                รายละเอียดการชำระเงิน
              </Typography>
              <TextField
                label="จำนวนเงิน"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                inputProps={{ min: 0, step: '0.01' }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>ธนาคาร</InputLabel>
                <Select
                  value={selectedBank}
                  onChange={(e) => setSelectedBank(e.target.value)}
                  label="ธนาคาร"
                >
                  {bankList && bankList.length > 0 ? (
                    bankList.map((bank) => (
                      <MenuItem
                        key={bank.id}
                        value={bank.id}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          py: 1,
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {bank.bankName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ชื่อบัญชี: {bank.accountName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          เลขที่บัญชี: {bank.accountNumber}
                        </Typography>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No banks available</MenuItem>
                  )}
                </Select>
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <DatePicker
                    label="วันที่ชำระเงิน"
                    value={paymentDateTime}
                    onChange={(newValue) => setPaymentDateTime(newValue)}
                    format="dd/MM/yyyy"
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <TimePicker
                    label="เวลา"
                    value={paymentDateTime}
                    onChange={(newValue) => setPaymentDateTime(newValue)}
                    ampm={false}
                    format="HH:mm"
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                    sx={{ flex: 1 }}
                  />
                </Box>
              </LocalizationProvider>
            </Box>

            {/* Image Preview */}
            {previewUrl && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Image Preview
                </Typography>
                <img
                  src={previewUrl}
                  alt="Preview of selected transfer slip"
                  style={{ maxWidth: '100%', height: 'auto', borderRadius: 4 }}
                />
                <IconButton
                  color="error"
                  onClick={handleRemoveFile}
                  aria-label="Remove selected file"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}

            {/* Upload Button */}
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              fullWidth
              disabled={
                !selectedFile || !amount || selectedBank === '' || uploadLoading
              }
              sx={{ mt: 2 }}
            >
              {uploadLoading ? (
                <CircularProgress size={24} />
              ) : (
                'ส่งข้อมูลแจ้งชำระเงิน'
              )}
            </Button>
            {uploadError && (
              <Typography color="error" sx={{ mt: 1 }}>
                {uploadError}
              </Typography>
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default UploadTransferSlip;
